import React from 'react';
import { Box, LinearProgress, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTranslation } from 'react-i18next';
import { FinanceBreakdownRowDto, FinanceTotalsDto } from '../../../../common/interfaces/invoicing.interface';
import { formatMoney } from '../../../../utilities/helper/money.helper';

const percent = (totals: FinanceTotalsDto) =>
  totals.chargedInMonth > 0 ? Math.round((totals.collectedOfIssued / totals.chargedInMonth) * 100) : null;

const Collected: React.FC<{ totals: FinanceTotalsDto }> = ({ totals }) => {
  const share = percent(totals);
  if (share === null) return <>—</>;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
      <LinearProgress
        variant="determinate"
        value={Math.min(share, 100)}
        aria-hidden
        sx={{
          width: 72,
          height: 6,
          borderRadius: 999,
          bgcolor: 'line',
          '& .MuiLinearProgress-bar': { bgcolor: 'success.main', borderRadius: 999 }
        }}
      />
      <span>{share} %</span>
    </Box>
  );
};

/**
 * The kindergarten by location: children, what was issued for the month and how much of
 * it is in, what came in during it, and what is owed today - with the total underneath.
 * The bar is the comparison between locations the old chart drew.
 */
const LocationsTable: React.FC<{
  rows: FinanceBreakdownRowDto[];
  totals: FinanceTotalsDto;
  onRowClick: (row: FinanceBreakdownRowDto) => void;
  /** A location's page lists its groups: each with its price list, the total named after the location. */
  grain?: 'location' | 'group';
  totalLabel?: string;
}> = ({ rows, totals, onRowClick, grain = 'location', totalLabel }) => {
  const { t } = useTranslation();
  const children = rows.reduce((sum, row) => sum + row.childCount, 0);
  // a group is red only when something of it is more than 30 days late; owing is not late
  const owedColor = (row: FinanceBreakdownRowDto) =>
    grain === 'group' ? (row.late30 ? 'error.main' : 'text.primary') : row.totals.outstanding > 0 ? 'error.main' : 'text.primary';
  const cell = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } as const;
  return (
    <Box sx={{ border: 1, borderColor: 'line', borderRadius: 4, overflow: 'hidden' }}>
      <Table size="small" aria-label={grain === 'group' ? t('finance.groups.ariaLabel') : t('finance.locations.ariaLabel')}>
        <TableHead>
          <TableRow sx={{ '& th': { bgcolor: 'surface.subtle', fontSize: 12, color: 'text.secondary' } }}>
            <TableCell>{grain === 'group' ? t('common.group') : t('common.location')}</TableCell>
            <TableCell align="right">{t('finance.locations.children')}</TableCell>
            <TableCell align="right">{t('finance.locations.issued')}</TableCell>
            <TableCell align="right">{t('finance.locations.collected')}</TableCell>
            <TableCell align="right">{t('finance.locations.received')}</TableCell>
            <TableCell align="right">{t('finance.locations.owed')}</TableCell>
            <TableCell sx={{ width: 40 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {!rows.length && (
            <TableRow>
              <TableCell colSpan={7}>
                <Typography sx={{ py: 3, textAlign: 'center', color: 'text.secondary' }}>
                  {grain === 'group' ? t('finance.noGroups') : t('finance.noLocations')}
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {rows.map(row => (
            <TableRow
              key={row.id ?? 'unassigned'}
              hover={!!row.id}
              sx={{ cursor: row.id ? 'pointer' : 'default' }}
              onClick={() => row.id && onRowClick(row)}
            >
              <TableCell>
                <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{row.name ?? t('finance.unassigned')}</Typography>
                {grain === 'group' && row.id ? (
                  // a group with no price list bills nobody without a price of their own
                  row.schemeName ? (
                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                      {t('finance.groups.scheme', { name: row.schemeName })}
                    </Typography>
                  ) : (
                    <Typography sx={{ fontSize: 12, color: 'warning.main' }}>{t('finance.groups.noScheme')}</Typography>
                  )
                ) : (
                  !!row.groupNames.length && (
                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{row.groupNames.join(', ')}</Typography>
                  )
                )}
              </TableCell>
              <TableCell align="right" sx={cell}>
                {row.childCount}
              </TableCell>
              <TableCell align="right" sx={cell}>
                {formatMoney(row.totals.chargedInMonth)}
              </TableCell>
              <TableCell align="right" sx={cell}>
                <Collected totals={row.totals} />
              </TableCell>
              <TableCell align="right" sx={cell}>
                {formatMoney(row.totals.paidInMonth)}
              </TableCell>
              <TableCell align="right" sx={{ ...cell, fontWeight: 600, color: owedColor(row) }}>
                {formatMoney(row.totals.outstanding)}
              </TableCell>
              <TableCell sx={{ color: 'text.secondary' }}>{row.id && <ChevronRightIcon aria-hidden fontSize="small" />}</TableCell>
            </TableRow>
          ))}
          {rows.length > 0 && (
            <TableRow sx={{ '& td': { bgcolor: 'surface.subtle', fontWeight: 600 } }}>
              <TableCell>{totalLabel ?? t('finance.locations.total')}</TableCell>
              <TableCell align="right" sx={cell}>
                {children}
              </TableCell>
              <TableCell align="right" sx={cell}>
                {formatMoney(totals.chargedInMonth)}
              </TableCell>
              <TableCell align="right" sx={cell}>
                <Collected totals={totals} />
              </TableCell>
              <TableCell align="right" sx={cell}>
                {formatMoney(totals.paidInMonth)}
              </TableCell>
              <TableCell align="right" sx={{ ...cell, color: totals.outstanding > 0 ? 'error.main' : 'text.primary' }}>
                {formatMoney(totals.outstanding)}
              </TableCell>
              <TableCell />
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
};

export default LocationsTable;
